import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import Spinner from '../components/Spinner'
import { Container } from '../components/Animations'
import Radom, { AccountServicesType, BusinessTypes, IVerification, VerificationStatus } from '../state/Radom'
import { generateRoundFlagIconFromCountryCode } from '../util/Flags'
import { Country } from 'country-state-city'
import { Countries } from './Verify/Countries'
import {
  MutedValue,
  SettingsCard,
  SettingsCardBody,
  SettingsCardDescription,
  SettingsCardHeader,
  SettingsCardTitle,
  SettingsHero,
  SettingsPage,
  SettingsRow,
  SettingsRowDescription,
  SettingsRows,
  SettingsRowTitle,
  SettingsSubtext,
  SettingsTitle
} from './Organization/SettingsLayout'

const VerificationBadge = styled.div<{ $status: VerificationStatus }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ $status }) => {
    switch ($status) {
    case 'Verified': return '#e8f5e9'
    case 'PendingApproval': return '#fff8e1'
    case 'Rejected': return '#fdecea'
    default: return '#f1f3f5'
    }
  }};
  color: ${({ $status }) => {
    switch ($status) {
    case 'Verified': return '#2e7d32'
    case 'PendingApproval': return '#f57c00'
    case 'Rejected': return '#c62828'
    default: return '#5c6370'
    }
  }};
`

const CountryValue = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #5c6370;
  font-size: 14px;
`

function accountServicesLabel(type: AccountServicesType): string {
  switch (type) {
  case AccountServicesType.crypto_only: return 'Crypto'
  case AccountServicesType.crypto_and_fiat: return 'Crypto & Fiat'
  default: return type
  }
}

function verificationStatusLabel(status: VerificationStatus): string {
  switch (status) {
  case 'Verified': return 'Verified'
  case 'PendingApproval': return 'Pending approval'
  case 'PendingVerificationSubmission': return 'Submission pending'
  case 'Rejected': return 'Rejected'
  default: return 'Not verified'
  }
}

function businessTypeLabel(type: string): string {
  return BusinessTypes[type as keyof typeof BusinessTypes] ?? type
}

function resolveCountry(alpha3: string): { name: string, iso2: string | undefined } {
  const name = Countries.find(c => c.alpha3 === alpha3)?.name ?? alpha3
  const iso2 = Country.getAllCountries().find(c => c.name === name)?.isoCode
  return { name, iso2 }
}

const VerificationSettings = observer((): React.ReactElement => {
  const [kybLoading, setKybLoading] = useState(true)
  const [verification, setVerification] = useState<IVerification | undefined>()

  useEffect(() => {
    setKybLoading(true)
    Radom.getVerification()
      .then(v => setVerification(v))
      .catch(err => console.error('Failed to load verification', err))
      .finally(() => setKybLoading(false))
  }, [])

  const alpha3 = verification?.registeredAddress?.country
  const resolved = alpha3 ? resolveCountry(alpha3) : undefined
  const countryName = resolved?.name
  const countryIso2 = resolved?.iso2

  return (
    <Container>
      <SettingsPage>

        <SettingsHero>
          <div>
            <SettingsTitle>Verification</SettingsTitle>
            <SettingsSubtext>
              Business verification status and submitted KYB details for this organization.
            </SettingsSubtext>
          </div>
          {!kybLoading && verification && (
            <VerificationBadge $status={verification.status}>
              {verificationStatusLabel(verification.status)}
            </VerificationBadge>
          )}
        </SettingsHero>

        <SettingsCard>
          <SettingsCardHeader>
            <div>
              <SettingsCardTitle>Business verification</SettingsCardTitle>
              <SettingsCardDescription>
                Your organization verification status and submitted details.
              </SettingsCardDescription>
            </div>
          </SettingsCardHeader>
          <SettingsCardBody>
            {kybLoading
              ? <Spinner />
              : <SettingsRows>
                {verification?.accountServices && (
                  <SettingsRow>
                    <div>
                      <SettingsRowTitle>Account type</SettingsRowTitle>
                    </div>
                    <MutedValue>{accountServicesLabel(verification.accountServices)}</MutedValue>
                  </SettingsRow>
                )}
                {verification?.name && (
                  <SettingsRow>
                    <div>
                      <SettingsRowTitle>Business name</SettingsRowTitle>
                    </div>
                    <MutedValue>{verification.name}</MutedValue>
                  </SettingsRow>
                )}
                {verification?.businessType && (
                  <SettingsRow>
                    <div>
                      <SettingsRowTitle>Business type</SettingsRowTitle>
                    </div>
                    <MutedValue>{businessTypeLabel(verification.businessType)}</MutedValue>
                  </SettingsRow>
                )}
                {verification?.website && (
                  <SettingsRow>
                    <div>
                      <SettingsRowTitle>Website</SettingsRowTitle>
                    </div>
                    <MutedValue>{verification.website}</MutedValue>
                  </SettingsRow>
                )}
                {countryName && (
                  <SettingsRow>
                    <div>
                      <SettingsRowTitle>Country</SettingsRowTitle>
                    </div>
                    <CountryValue>
                      {countryIso2 && generateRoundFlagIconFromCountryCode(countryIso2, 20)}
                      {countryName}
                    </CountryValue>
                  </SettingsRow>
                )}
                {!verification && (
                  <SettingsRow>
                    <div>
                      <SettingsRowTitle>Not started</SettingsRowTitle>
                      <SettingsRowDescription>
                        No verification has been submitted for this organization.
                      </SettingsRowDescription>
                    </div>
                  </SettingsRow>
                )}
              </SettingsRows>}
          </SettingsCardBody>
        </SettingsCard>

      </SettingsPage>
    </Container>
  )
})

export default VerificationSettings
